<template>
  <div class="gid-feed">
    <div class="d-flex justify-content-between">
      <h5 class="mb-2">
        <span class="d-inline-block mr-4">{{ $t('activity-feed.title') }}</span>
        <template v-if="show">
          <div
            class="d-inline-block"
            v-for="(type, key) in typesFiltered"
            :key="key"
            @click.alt.stop.prevent="() => checkOnly(key)"
          >
            <b-form-checkbox
              inline
              v-model="type.enabled"
              :disabled="type.loading"
            >
              <small>
                <component :is="type.icon" />
                {{ type.label }}
              </small>
            </b-form-checkbox>
          </div>
        </template>
      </h5>
      <b-form-checkbox inline switch v-model="show" class="gid-checkbox-right">
        <small class="text-muted">show</small>
      </b-form-checkbox>
    </div>
    <comments
      ref="comments"
      :job-api-id="this.job.api_id"
      disable-list-comments
      disable-new-button
      v-on="types.comment.addEvents"
    />
    <b-collapse v-model="show">
      <div class="gid-feed__viewport bg-white rounded border">
        <table class="w-100 mb-2">
          <template v-for="({ date, items }, dateIndex) of itemsByDate">
            <tr :key="`day-${dateIndex}`">
              <td colspan="3" class="gid-feed__heading pt-2">
                <h5
                  class="font-weight-light border-bottom border-top bg-light rounded-top px-2 py-1 mb-0"
                >
                  {{ date | moment('ll') }}
                  <small class="text-muted">
                    - {{ date | moment('from') }}
                  </small>
                </h5>
              </td>
            </tr>
            <tr
              v-for="(item, itemIndex) of items"
              :key="`day-${dateIndex}-item-${itemIndex}`"
              :class="{ 'border-bottom': itemIndex != items.length - 1 }"
            >
              <td class="px-2">
                <small
                  class="d-inline-block bg-light border rounded-pill px-2 text-nowrap"
                  :title="item.time | moment('LTS')"
                  v-b-tooltip.hover
                >
                  {{ item.time | moment('LT') }}
                </small>
              </td>
              <td class="pr-2">
                <small
                  class="d-inline-block bg-light border rounded-pill px-2 text-nowrap"
                >
                  <component :is="types[item.type].icon" variant="primary" />
                  {{ types[item.type].label }}
                </small>
              </td>
              <td class="w-100 pr-2">
                <component
                  :is="types[item.type].component"
                  :value="item.value"
                  v-on="types[item.type].on"
                  class="gid-feed__item"
                />
              </td>
            </tr>
          </template>
        </table>
      </div>
    </b-collapse>
    <hr class="my-2" />
  </div>
</template>

<script>
import {
  BIconChatLeftText,
  BIconJournalText,
  BIconSignpost,
  BIconEnvelope,
  BIconStickies,
  BIconBag,
  BIconSliders,
  BIconBoxArrowRight,
} from 'bootstrap-vue';
import moment from 'moment';
import { mapGetters } from 'vuex';

import Comments from './Comments.vue';

import CommentActivity, { fetchComments } from './activity/CommentActivity.vue';
import LogActivity, { fetchLogs } from './activity/LogActivity.vue';
import ProductActivity, {
  fetchProductOrders,
} from './activity/ProductActivity.vue';
import CancellationActivity, {
  fetchCancellations,
} from './activity/CancellationActivity.vue';
import EmailActivity, { fetchEmails } from './activity/EmailActivity.vue';
import TicketArticlesActivity, {
  fetchTicketArticles,
} from './activity/TicketArticlesActivity.vue';
import TicketActivity, { fetchTickets } from './activity/TicketActivity.vue';
import StateActivity, { fetchStates } from './activity/StateActivity.vue';
import SubmissionActivity, {
  fetchSubmissions,
} from './activity/SubmissionActivity.vue';
import { CommentActionTypeEnum } from '@gid/models/dist/entities/comment';
import { UserRoleEnum } from '@gid/models';

export default {
  components: {
    BIconChatLeftText,
    BIconJournalText,
    BIconSignpost,
    BIconEnvelope,
    BIconStickies,
    BIconBag,
    BIconSliders,
    BIconBoxArrowRight,
    Comments,
    CommentActivity,
    LogActivity,
    ProductActivity,
    CancellationActivity,
    EmailActivity,
    TicketArticlesActivity,
    TicketActivity,
    StateActivity,
    SubmissionActivity,
  },
  props: {
    job: Object,
    refreshTickets: Boolean,
  },
  data() {
    return {
      show: true,
      types: {
        comment: {
          icon: 'BIconChatLeftText',
          label: this.$t('activity-feed.type.comment'),
          component: 'CommentActivity',
          fetch: fetchComments,
          on: {
            editComment: (comment) => {
              this.$refs.comments.editComment(comment);
            },
            deleteComment: (comment) => {
              this.$refs.comments.deleteComment(comment);
            },
          },
          addEvents: {
            'comment-update': (comment) => {
              const item = this.items.find(
                ({ type, value }) =>
                  type == 'comment' && value.id == comment.id,
              );
              if (
                item.value.actionType == CommentActionTypeEnum.GENERIC &&
                comment.actionType != item.value.actionType
              ) {
                this.$emit('comment-action');
              }
              item.value = comment;
            },
            'comment-delete': (comment) => {
              this.items = this.items.filter(
                ({ type, value }) =>
                  type != 'comment' || value.id != comment.id,
              );
            },
            'comment-add': (comment) => {
              this.items.push({
                time: new Date(comment.createdAt),
                type: 'comment',
                value: comment,
              });
            },
            'comment-action': () => {
              this.$emit('comment-action');
            },
          },
          visibleFor: [
            UserRoleEnum.ADMIN,
            UserRoleEnum.BRAND,
            UserRoleEnum.CUSTOMER,
          ],
        },
        log: {
          icon: 'BIconJournalText',
          label: this.$t('activity-feed.type.log'),
          component: 'LogActivity',
          fetch: fetchLogs,
          visibleFor: [UserRoleEnum.ADMIN],
        },
        cr: {
          icon: 'BIconSignpost',
          label: this.$t('activity-feed.type.cr'),
          component: 'CancellationActivity',
          fetch: fetchCancellations,
          visibleFor: [UserRoleEnum.ADMIN, UserRoleEnum.BRAND],
        },
        email: {
          icon: 'BIconEnvelope',
          label: this.$t('activity-feed.type.email'),
          component: 'EmailActivity',
          fetch: fetchEmails,
          visibleFor: [
            UserRoleEnum.ADMIN,
            UserRoleEnum.BRAND,
            UserRoleEnum.CUSTOMER,
          ],
        },
        ticketArticles: {
          icon: 'BIconStickies',
          label: this.$t('activity-feed.type.ticket'),
          component: 'TicketArticlesActivity',
          fetch: fetchTicketArticles,
          visibleFor: [UserRoleEnum.ADMIN],
        },
        productOrder: {
          icon: 'BIconBag',
          label: this.$t('activity-feed.type.order'),
          component: 'ProductActivity',
          fetch: fetchProductOrders,
          visibleFor: [UserRoleEnum.ADMIN, UserRoleEnum.BRAND],
        },
        state: {
          icon: 'BIconBoxArrowRight',
          label: this.$t('activity-feed.type.state'),
          component: 'StateActivity',
          fetch: fetchStates,
          visibleFor: [UserRoleEnum.ADMIN, UserRoleEnum.BRAND],
        },
        submission: {
          icon: 'BIconBoxArrowRight',
          label: this.$t('activity-feed.type.submission'),
          component: 'SubmissionActivity',
          fetch: fetchSubmissions,
          visibleFor: [
            UserRoleEnum.ADMIN,
            UserRoleEnum.BRAND,
            UserRoleEnum.CUSTOMER,
          ],
        },
      },
      items: [],
    };
  },
  computed: {
    ...mapGetters(['environment', 'user']),
    itemsFiltered() {
      return this.items.filter(({ type }) => this.typesFiltered[type].enabled);
    },
    typesFiltered() {
      return Object.fromEntries(
        Object.entries(this.types).filter(([, type]) =>
          type.visibleFor.includes(this.user?.role),
        ),
      );
    },
    itemsByDate() {
      const itemsSorted = [...this.itemsFiltered];
      itemsSorted.sort((a, b) => b.time - a.time);
      const itemsByDate = [];
      let dayItems;
      for (const item of itemsSorted) {
        if (!dayItems || !moment(dayItems.date).isSame(item.time, 'day')) {
          dayItems = {
            date: item.time,
            items: [],
          };
          itemsByDate.push(dayItems);
        }
        dayItems.items.push(item);
      }
      return itemsByDate;
    },
  },
  watch: {
    refreshTickets(value) {
      if (value) this.delayedLoadData();
    },
  },
  created() {
    this.loadData();
  },
  methods: {
    hide() {
      this.show = false;
    },
    addActivity(type) {
      if (type == 'comment') {
        this.$refs.comments.showAddComment();
      }
    },
    checkOnly(type) {
      if (!this.types[type].loading) {
        this.types = Object.fromEntries(
          Object.entries(this.types).map(([key, values]) => {
            values.enabled = key === type;
            return [key, values];
          }),
        );
      }
    },
    loadData() {
      Object.keys(this.typesFiltered).forEach((type) => {
        const typeDef = this.typesFiltered[type];
        this.$set(typeDef, 'enabled', true);
        this.$set(typeDef, 'loading', true);
        typeDef
          .fetch?.({ job: this.job, env: this.environment, user: this.user })
          .then((typeItems) => {
            this.items.push(
              ...typeItems.map((item) => ({
                ...item,
                type,
              })),
            );
            typeDef.loading = false;
          });
      });
    },
    delayedLoadData() {
      setTimeout(() => {
        this.items = [];
        this.loadData();
      }, 5000);
    },
  },
};
</script>

<style lang="scss">
.gid-feed {
  &__viewport {
    overflow-y: auto;
    min-height: 200px;
    height: 400px;
    max-height: fit-content;
    resize: vertical;

    > *:first-child {
      margin-top: calc(-0.5rem - 2px);
    }
  }

  &__heading {
    position: sticky;
    top: calc(-0.5rem - 2px);
    z-index: 6;
  }

  // reset parent table definitions
  .table & td {
    padding: 0 0 0.2rem;
    border: none;
  }

  .table-striped tbody & tr:nth-of-type(odd) {
    background: none;
  }

  @media (hover: hover) {
    &__item:not(:hover) &__details {
      display: none;
    }
  }
}
</style>
